import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flag, Timer, ShieldCheck, Trophy, ChevronRight, ArrowUpRight, BadgeCheck, HeartHandshake } from "lucide-react";
import KineticHero from "@/components/KineticHero";
import { EditorialMarquee, TextTicker } from "@/components/Marquee";
import PressSection from "@/components/PressSection";
import LenoxSection from "@/components/LenoxSection";
import F1Section from "@/components/F1Section";
import PartnersSection from "@/components/PartnersSection";
import SpeedWeekSection from "@/components/SpeedWeekSection";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { StatCounter, SectionTitle } from "@/components/Shared";
import { STATS, MANIFESTO, PACKAGES, IMAGES, LOGOS, CREDENTIALS } from "@/data/site";

const stepIcons = [Flag, ShieldCheck, Timer, Trophy];
const credIcons = { shield: ShieldCheck, badge: BadgeCheck, heart: HeartHandshake };
const steps = [
  { t: "Arrive", d: "Walk in any time we're open. No booking needed for individuals or groups of friends." },
  { t: "Sign", d: "Complete our quick digital liability waiver at the front desk." },
  { t: "Gear Up", d: "We provide a helmet and a full safety briefing before you head out on track." },
  { t: "Race", d: "Hit the grid, chase the apex and chase your fastest lap." },
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      <KineticHero />

      {/* Stats band */}
      <section className="relative bg-[#050505] py-16 md:py-20">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-10 px-5 md:grid-cols-4 md:px-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <StatCounter {...s} />
            </Reveal>
          ))}
        </div>
      </section>

      <TextTicker text="ARRIVE · SIGN · GEAR UP · RACE ·" />

      {/* FIA credentials */}
      <section data-testid="credentials-section" className="relative overflow-hidden bg-[#050505] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="OFFICIAL & SANCTIONED" title="The Bahamas' Only FIA-Approved Motorsports Club" className="mb-14" />
          <StaggerGroup className="grid gap-6 md:grid-cols-3">
            {CREDENTIALS.map((c) => {
              const Icon = credIcons[c.icon] || ShieldCheck;
              return (
                <StaggerItem key={c.t}>
                  <div className="group h-full rounded-xl border border-white/10 bg-obsidian p-8 transition-all hover:-translate-y-2 hover:border-kbyellow/50">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-kbyellow/10 text-kbyellow transition-colors group-hover:bg-kbyellow group-hover:text-black">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-display text-3xl tracking-wide text-white">{c.t}</h3>
                    <p className="mt-3 leading-relaxed text-white/55">{c.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Manifesto chapters */}
      <section data-testid="manifesto-section" className="relative overflow-hidden bg-obsidian py-24 md:py-36">
        <div className="checker-texture absolute inset-0 opacity-[0.35]" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="THE KARTING BAHAMAS DIFFERENCE" title="Not A Ride. A Race." className="mb-16 md:mb-24" />
          <div className="grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 md:grid-cols-2">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.no} delay={(i % 2) * 0.1}>
                <div className="group h-full bg-[#0A0A0A] p-8 transition-colors hover:bg-[#101010] md:p-12">
                  <div className="flex items-start justify-between">
                    <span className="font-anton text-6xl text-white/10 transition-colors group-hover:text-kbcyan/30">{m.no}</span>
                    <ArrowUpRight className="h-6 w-6 text-white/20 transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-kbyellow" />
                  </div>
                  <h3 className="mt-6 font-display text-4xl tracking-wide text-white">{m.title}</h3>
                  <p className="mt-4 leading-relaxed text-white/55">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature image split */}
      <section className="relative bg-[#050505] py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:grid-cols-2 md:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-xl">
              <img src={IMAGES.trackDrone} alt="Tyre-lined track corner" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute bottom-4 left-4 rounded-lg bg-black/60 px-4 py-2 font-display text-lg tracking-widest text-kbcyan backdrop-blur-md">
                150,000+ SQ FT
              </div>
            </div>
          </Reveal>
          <div>
            <SectionTitle eyebrow="THE CIRCUIT" title="A Track Built On A Parking Lot" />
            <p className="mt-6 max-w-lg leading-relaxed text-white/60">
              Karting Bahamas is set up across the 150,000+ square foot Southern Parking Lot of the
              Thomas A. Robinson National Stadium in Nassau. The circuit is shaped and protected entirely
              by hundreds of recycled used car tyres — a genuinely grassroots, hand-built racing venue.
            </p>
            <ul className="mt-8 space-y-4">
              {["Two tracks: main circuit + junior track", "Built from recycled used car tyres", "Marshaled, safety-first sessions", "Marshal training and opportunities", "Arrive-and-drive, no booking needed"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-white/80">
                  <span className="h-2 w-2 rotate-45 bg-kbyellow" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/experience")}
              data-testid="home-experience-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-xl border border-kbcyan px-6 py-3 font-display text-xl tracking-wide text-kbcyan transition-colors hover:bg-kbcyan hover:text-black"
            >
              Discover The Experience
              <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section data-testid="how-it-works" className="relative overflow-hidden bg-obsidian py-24 md:py-32">
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="ARRIVE & DRIVE" title="Racing In Four Steps" className="mb-16" />
          <StaggerGroup className="grid gap-6 md:grid-cols-4">
            {steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <StaggerItem key={s.t}>
                  <div className="group h-full rounded-xl border border-white/10 bg-[#0A0A0A] p-8 transition-all hover:-translate-y-2 hover:border-kbcyan/50">
                    <div className="flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-kbcyan/10 text-kbcyan transition-colors group-hover:bg-kbcyan group-hover:text-black">
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="font-anton text-3xl text-white/10">0{i + 1}</span>
                    </div>
                    <h3 className="mt-6 font-display text-3xl tracking-wide text-white">{s.t}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">{s.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <EditorialMarquee words={["SPEED", "PRECISION", "ADRENALINE", "GRIP", "GLORY"]} />

      {/* Packages preview */}
      <section className="relative bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionTitle eyebrow="GROUP EVENTS" title="Own The Whole Track" />
            <button
              onClick={() => navigate("/group-events")}
              data-testid="home-packages-cta"
              className="group inline-flex items-center gap-2 self-start font-display text-2xl tracking-wide text-kbyellow md:self-end"
            >
              View All Packages
              <ChevronRight className="h-6 w-6 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <StaggerGroup className="grid gap-6 md:grid-cols-3">
            {PACKAGES.map((p) => (
              <StaggerItem key={p.name}>
                <div className="group relative h-full overflow-hidden rounded-xl border border-white/10">
                  <img src={p.image} alt={p.name} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <span className="font-display text-sm tracking-[0.25em] text-kbcyan">{p.ideal}</span>
                    <h3 className="mt-1 font-display text-3xl tracking-wide text-white">{p.name}</h3>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* EduKarting teaser */}
      <section data-testid="edukarting-teaser" className="relative overflow-hidden bg-obsidian py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:grid-cols-2 md:px-10">
          <div>
            <img src={LOGOS.edukarting} alt="EduKarting Bahamas" className="h-24 w-auto" />
            <h2 className="mt-6 font-display text-5xl leading-[0.9] tracking-tight text-white md:text-6xl">
              Building The Next <span className="text-kbyellow">Generation</span>
            </h2>
            <p className="mt-6 max-w-lg leading-relaxed text-white/60">
              Karting Bahamas is the new home of EduKarting Bahamas — a registered non-profit (NPO) and FIA
              Karting club. Every dollar of proceeds from the track goes directly to funding the EduKarting
              programme, nurturing young Bahamian drivers from grassroots to the grid.
            </p>
            <button
              onClick={() => navigate("/edukarting")}
              data-testid="home-edukarting-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-kbyellow px-6 py-3 font-display text-xl tracking-wide text-black transition-transform hover:scale-[1.03]"
            >
              Explore EduKarting
              <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <Reveal>
            <div className="relative overflow-hidden rounded-xl">
              <img src={IMAGES.eduKids} alt="Youth karting" className="aspect-square w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <SpeedWeekSection />

      <LenoxSection />

      <F1Section />

      <PartnersSection />

      <PressSection bg="bg-obsidian" />

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-[#050505] py-28 md:py-40">
        <img src={IMAGES.motionBlur2} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-[#050505]" />
        <div className="relative mx-auto max-w-[1400px] px-5 text-center md:px-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-6xl leading-[0.85] tracking-tight text-white md:text-8xl"
          >
            Ready To <span className="text-kbcyan">Send It?</span>
          </motion.h2>
          <p className="mx-auto mt-6 max-w-xl leading-relaxed text-white/60">
            No booking. No fuss. Just arrive, sign the waiver, donate on track and race.
            Cashless venue — credit and debit cards only.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate("/pricing")}
              data-testid="home-final-pricing-btn"
              className="rounded-xl bg-kbcyan px-8 py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.03]"
            >
              See Pricing
            </button>
            <button
              onClick={() => navigate("/contact")}
              data-testid="home-final-contact-btn"
              className="rounded-xl border border-white/25 px-8 py-4 font-display text-2xl tracking-wide text-white transition-colors hover:border-kbyellow hover:text-kbyellow"
            >
              Find The Track
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
